import React, { useCallback, useMemo } from 'react';
import type { PageDetailOut } from '../../types/catalog';
import type { ContentWithSections, SectionGrid, SectionMeta } from '../../types/pageTypes';
import { Volume2 } from 'lucide-react';
import { speakTamil, speakTamilSequence, stopSpeak } from '../../utils/tts';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';

interface ReviewGridV2RendererProps {
  page: PageDetailOut;
}

function getSections(page: PageDetailOut) {
  const content = page.page_json_v1?.content as ContentWithSections | undefined;
  return content?.sections ?? [];
}

function getGridCols(itemType: string | undefined, columns?: number): string {
  const cols = columns ?? 4;
  switch (itemType) {
    case 'letter':
      return 'grid-cols-3 md:grid-cols-6';
    case 'word':
      return 'grid-cols-2 md:grid-cols-4';
    case 'sentence':
      return 'grid-cols-1 md:grid-cols-3';
    default:
      return cols <= 2 ? 'grid-cols-2' : cols <= 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-3 md:grid-cols-6';
  }
}

export const ReviewGridV2Renderer: React.FC<ReviewGridV2RendererProps> = ({ page }) => {
  const sections = getSections(page);
  const meta = sections.find((s) => s.type === 'meta') as SectionMeta | undefined;
  const gridSections = sections.filter((s) => s.type === 'grid') as SectionGrid[];

  const metaPills = useMemo(() => {
    const items = meta?.items ?? [];
    return items
      .map((i: { label?: string; value?: string }) => `${i.label ?? ''} ${i.value ?? ''}`.trim())
      .filter(Boolean);
  }, [meta]);

  const hasPlayAll = (page.page_json_v1?.interactions ?? []).some(
    (i: { type?: string }) => i.type === 'tts.play_all'
  );

  const allPlayableTexts = useMemo(() => {
    const out: string[] = [];
    for (const g of gridSections) {
      for (const item of g.items ?? []) {
        const t = (item.tts ?? item.text)?.trim();
        if (t) out.push(t);
      }
    }
    return out;
  }, [gridSections]);

  const onPlayAll = useCallback(() => {
    speakTamilSequence(allPlayableTexts);
  }, [allPlayableTexts]);

  const onPlayItem = useCallback((text: string) => {
    stopSpeak();
    speakTamil(text);
  }, []);

  const hasAnyContent = metaPills.length > 0 || gridSections.some((g) => (g.items?.length ?? 0) > 0);
  const pageTitle =
    (page.page_title ?? (page.page_json_v1 as { page_title?: string })?.page_title ?? 'Review') as string;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
        {pageTitle}
      </h1>

      {metaPills.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {metaPills.map((label, i) => (
            <span
              key={i}
              className="px-3 py-1.5 rounded-full bg-primary-100 dark:bg-primary-900/40 text-primary-800 dark:text-primary-200 text-sm font-medium"
            >
              {label}
            </span>
          ))}
        </div>
      )}

      {hasPlayAll && allPlayableTexts.length > 0 && (
        <button
          type="button"
          onClick={onPlayAll}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 border-primary-500 bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
        >
          <Volume2 className="w-5 h-5" />
          Play All
        </button>
      )}

      {gridSections.map((grid, gIdx) => {
        const items = grid.items ?? [];
        const itemType = (grid.item_type ?? 'letter') as 'letter' | 'word' | 'sentence';
        const gridCols = getGridCols(grid.item_type, grid.columns);
        if (items.length === 0) return null;
        return (
          <div key={gIdx} className="space-y-3">
            {grid.title && (
              <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">{grid.title}</h3>
            )}
            <div className={`grid gap-4 ${gridCols}`}>
              {items.map((item, i) => (
                <GridItemCard
                  key={item.id ?? i}
                  item={item}
                  itemType={itemType}
                  onPlayItem={onPlayItem}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

interface GridItemCardProps {
  item: { text?: string; tts?: string; image?: { url: string; alt?: string } | null; [k: string]: unknown };
  itemType: 'letter' | 'word' | 'sentence';
  onPlayItem: (text: string) => void;
}

function GridItemCard({ item, itemType, onPlayItem }: GridItemCardProps) {
  const text = item.text ?? '';
  const tts = (item.tts ?? item.text)?.trim();
  const image = item.image && typeof item.image === 'object' && item.image.url ? item.image : null;

  const play = useCallback(() => {
    if (tts) onPlayItem(tts);
    else if (text) onPlayItem(text);
  }, [tts, text, onPlayItem]);

  if (itemType === 'word') {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-600 bg-white dark:bg-gray-800 shadow-sm overflow-hidden hover:shadow-md transition-shadow flex flex-col">
        <div className="aspect-[4/3] bg-slate-100 dark:bg-slate-700/50">
          <ImageWithFallback
            src={image?.url ?? null}
            alt={image?.alt ?? text}
            className="h-full w-full object-cover"
          />
        </div>
        <div className="p-3 flex items-center justify-between gap-2 min-h-[3rem]">
          <span className="text-xl font-semibold text-slate-900 dark:text-white flex-1 truncate">
            {text}
          </span>
          <button
            type="button"
            onClick={play}
            aria-label={`Play: ${text}`}
            className="h-10 w-10 rounded-full bg-sky-100 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300 flex items-center justify-center hover:bg-sky-200 dark:hover:bg-sky-800/50 transition-colors shrink-0"
          >
            <Volume2 className="w-5 h-5" />
          </button>
        </div>
      </div>
    );
  }

  if (itemType === 'sentence') {
    return (
      <div className="flex items-center gap-3 p-4 rounded-2xl bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 shadow-sm hover:border-primary-400 dark:hover:border-primary-500 transition-colors text-left">
        <span className="text-lg font-medium text-gray-900 dark:text-white flex-1">
          {text}
        </span>
        <button
          type="button"
          onClick={play}
          aria-label={`Play: ${text}`}
          className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 shrink-0"
        >
          <Volume2 className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center p-4 rounded-2xl bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 shadow-sm hover:border-primary-400 dark:hover:border-primary-500 transition-colors min-h-[100px]">
      <span className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-2">
        {text}
      </span>
      <button
        type="button"
        onClick={play}
        aria-label={`Play: ${text}`}
        className="p-2 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-800/50 transition-colors"
      >
        <Volume2 className="w-5 h-5" />
      </button>
    </div>
  );
}
